<script setup lang="ts">
import {RouterView} from 'vue-router'
import {setJhaWcTheme, JHA_THEMES} from '@banno/jha-wc/src/styles/theme-provider.js'
import {setJhTheme} from '@jack-henry/jh-ui/utils/themeProvider.js'
import {VsgToastContainer} from '@jack-henry/jha-vue-components'
import {onMounted, onUnmounted, ref} from 'vue'
import {$inj, $injByInterface} from '@/common/decorators/depinject'
import type IWorkspaceMetadataStore from '@/common/services/Workspace/IWorkspaceMetadataStore'
import BltToaster from '@/common/components/bltToaster/bltToaster.vue'
import {CacheStore} from '@/common/services/connect/CacheStore'
import UatBanner from '@/common/components/uatBanner/uatBanner.vue'
import {useLDFlag, useLDReady, useLDClient} from 'launchdarkly-vue-client-sdk'
import LogService from '@/common/services/Log/LogService'
import JackhenryLogo from '@/branchmanager/jackhenryLogo.vue'

const showLdDebug = import.meta.env.VITE_PRODUCTION !== 'true' || window.LD_INSPECTOR_ENABLED === true

const workspaceMetadataStore = $injByInterface<IWorkspaceMetadataStore>('IWorkspaceMetadataStore')
const showNewTheme = useLDFlag('openanywhere-jha-design-system', false)
const oaLogoRedesign = useLDFlag('oa-logo-redesign', false)
const ldClient = useLDClient();
const ldReady = useLDReady()
const logService = $inj(LogService)
const allowRetry = ref(true)

let TIMEOUT: NodeJS.Timeout;

CacheStore().clear()
onMounted(() => {
  TIMEOUT = setTimeout(() => {
    allowRetry.value = false
  }, 2000)

  requestAnimationFrame(waitForLdReady)
  setJhTheme()
})

onUnmounted(() => {
  clearTimeout(TIMEOUT)
})

const waitForLdReady = () => {
  if (!ldReady.value && allowRetry.value) {
    requestAnimationFrame(waitForLdReady)
    logService.debug('LaunchDarkly is not ready at this time to determine the theme')
  } else {
    setJhaWcTheme(showNewTheme.value ? JHA_THEMES.DESIGN_SYSTEM : JHA_THEMES.JHA_ADMIN)
    // Inject the ldClient into the window in non-production environments and enable a feature flag inspector
    if (showLdDebug && typeof window !== 'undefined') {
      window.ldClient = ldClient;
    }
  }
}
</script>

<template>
  <uat-banner v-if="workspaceMetadataStore.languageConstants?.SHOW_UAT_BANNER"></uat-banner>
  <jackhenry-logo v-if="oaLogoRedesign"/>
  <RouterView v-slot="{Component}">
    <suspense timeout="0">
      <template #default>
        <component :is="Component"></component>
      </template>
    </suspense>
  </RouterView>
  <BltToaster v-if="workspaceMetadataStore.toast.showToast" />
  <vsg-toast-container class="toast-radius" />
</template>